/**
 * Which AI assistant sent a visitor here.
 *
 * People now ask ChatGPT, Perplexity, Gemini, Claude and Copilot who to hire,
 * and those answers link to us. The visit shows up in analytics as a referrer,
 * but a quote requested three pages later has lost it. So, exactly like the ad
 * click in lib/click-ids.ts: copy it into a first-party cookie on arrival, send
 * it with the quote, and write it onto the Printavo record. "How many of our
 * quotes started in an AI assistant" then has an answer.
 *
 * ChatGPT puts `utm_source=chatgpt.com` on the links it shows; the others mostly
 * arrive as a plain Referer. Google's AI Overviews and AI Mode arrive as
 * google.com and CANNOT be told apart from ordinary search, so they are not
 * counted here. That is a limit of the web, not of this code.
 */

export type AiSourceId =
  | "chatgpt" | "perplexity" | "gemini" | "claude" | "copilot"
  | "grok" | "meta_ai" | "you" | "phind" | "duckai" | "mistral";

type Def = { label: string; hosts: string[]; utm: string[] };

export const AI_SOURCES: Record<AiSourceId, Def> = {
  chatgpt: { label: "ChatGPT", hosts: ["chatgpt.com", "chat.openai.com"], utm: ["chatgpt.com", "chatgpt"] },
  perplexity: { label: "Perplexity", hosts: ["perplexity.ai"], utm: ["perplexity", "perplexity.ai"] },
  gemini: { label: "Gemini", hosts: ["gemini.google.com", "bard.google.com"], utm: ["gemini"] },
  claude: { label: "Claude", hosts: ["claude.ai"], utm: ["claude", "claude.ai"] },
  copilot: { label: "Copilot", hosts: ["copilot.microsoft.com"], utm: ["copilot"] },
  grok: { label: "Grok", hosts: ["grok.com"], utm: ["grok"] },
  meta_ai: { label: "Meta AI", hosts: ["meta.ai"], utm: ["meta_ai"] },
  you: { label: "You.com", hosts: ["you.com"], utm: ["you.com"] },
  phind: { label: "Phind", hosts: ["phind.com"], utm: ["phind"] },
  duckai: { label: "DuckDuckGo AI", hosts: ["duck.ai"], utm: ["duck.ai"] },
  mistral: { label: "Le Chat", hosts: ["chat.mistral.ai"], utm: ["mistral"] },
};

export const AI_SOURCE_IDS = Object.keys(AI_SOURCES) as [AiSourceId, ...AiSourceId[]];

const hostMatches = (host: string, h: string) => host === h || host.endsWith(`.${h}`);

/** Classify a visit from its query string and referrer; null when it is not from an AI assistant. */
export function classifyAiSource(search: string, referrer: string): AiSourceId | null {
  const utm = new URLSearchParams(search).get("utm_source")?.trim().toLowerCase();
  let host = "";
  try { host = referrer ? new URL(referrer).hostname.toLowerCase() : ""; } catch { /* not a URL */ }
  for (const id of AI_SOURCE_IDS) {
    const d = AI_SOURCES[id];
    if (utm && d.utm.includes(utm)) return id;
    if (host && d.hosts.some((h) => hostMatches(host, h))) return id;
  }
  return null;
}

/** Validate a value from a form or request body. Only known ids are ever accepted. */
export function parseAiSource(raw: unknown): AiSourceId | null {
  return typeof raw === "string" && (AI_SOURCE_IDS as string[]).includes(raw) ? (raw as AiSourceId) : null;
}

/** The single line added to a Printavo production note, or null. */
export function aiSourceNoteFromField(raw: unknown): string | null {
  const id = parseAiSource(raw);
  return id ? `Found via AI: ${AI_SOURCES[id].label}` : null;
}

// ── browser side ────────────────────────────────────────────────────────────

const COOKIE = "aa_ai";
const MAX_AGE_DAYS = 30;

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const hit = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.slice(name.length + 1)) : null;
}

/**
 * Remember an AI arrival. An ordinary pageview with no AI source must NOT wipe
 * the one already stored, or the quote requested later would lose it.
 */
export function captureAiSource(
  search: string = typeof window === "undefined" ? "" : window.location.search,
  referrer: string = typeof document === "undefined" ? "" : document.referrer,
): AiSourceId | null {
  if (typeof document === "undefined") return null;
  const id = classifyAiSource(search, referrer);
  if (!id) return null;
  try {
    document.cookie =
      `${COOKIE}=${encodeURIComponent(id)}; path=/; max-age=${MAX_AGE_DAYS * 24 * 60 * 60}; SameSite=Lax` +
      (location.protocol === "https:" ? "; Secure" : "");
  } catch { /* cookies blocked: attribution is lost for this visitor, nothing else breaks */ }
  return id;
}

/** The AI assistant that brought this visitor here, if we still hold one. */
export function storedAiSource(): AiSourceId | null {
  return parseAiSource(readCookie(COOKIE));
}
